import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip } from 'antd';
import {
  atomOneDark,
  atomOneLight,
} from '@fengkx/react-syntax-highlighter/dist/esm/styles/hljs';
import HtmlViewTemplate from '@/components/html-viewer/view-template';
import { markdownToPlainText } from '@shared/utils';
import copy from 'copy-to-clipboard';
import { Dropdown, Segmented, Skeleton } from 'antd';
import { request } from '@/common';
import Other from '@/common/helper/other';
import { SVGPureIcon } from '@/ui';
import ScrollView from '@/ui/scroll-view';
import Markdown from '@/components/markdown';
import { HelperCore } from '@/common/dependencies';
import SyntaxHighlighter from '@fengkx/react-syntax-highlighter';
import './index.scss';
import ScaleBox from '../scale-box';
import { useConversationContext } from '../chat-history/context';
import QrcodeShare from '../qrcode-share';
import fullScreenModalStore from '../full-screen-modal/store';
import CodeBlock from './components/code-block';

const isWeixin = /micromessenger/i.test(navigator.userAgent);

const CardHtml = observer(props => {
  const { data, mcpStore, type = 'agent', event } = props;

  const [currentVal, setCurrentVal] = useState('result_web');
  const [isPhone] = useState(HelperCore.isPhone());
  const { isMcpShare } = useConversationContext();

  const refScroll = useRef(null);
  const refIframe = useRef(null);
  const [shareId, setShareId] = useState('');
  const isDark = true;
  const {
    content,
    msg_id = '',
    status,
    title = '',
    url,
    share_url,
  } = event || {};

  const actionItems = [
    {
      label: '网页结果',
      value: 'result_web',
      disabled: !(status == 'success' || status == 'failed'),
      designed: ['result_web'],
    },

    {
      label: '代码结果',
      value: 'code',
      disabled: status == 'failed',
      designed: ['result_web', 'result_chart', 'result_mindmap', 'result_ppt'],
    },
  ];

  const highlighterDark = {
    className: 'highlighter-box highlighter-box-dark',
    style: atomOneDark,
    showLineNumbers: true,
  };
  const highlighterLight = {
    className: 'highlighter-box highlighter-box-light',
    style: atomOneLight,
    showLineNumbers: true,
  };
  const highlighterConfig = isDark ? highlighterDark : highlighterLight;

  useEffect(() => {
    if (!share_url) {
      return;
    }

    const params = new URLSearchParams(share_url);
    const id = params.get('id');
    if (id) {
      setShareId(id);
    } else {
      const host = share_url;
      if (/^https\:\/\/(\w{6,})\.n\.cn/.test(host)) {
        setShareId(RegExp.$1);
      }
    }
  }, [share_url]);

  useEffect(() => {
    if (status === 'success' || status === 'failed') {
      setCurrentVal('result_web');
    } else {
      setCurrentVal('code');
    }
  }, [status]);

  const handleCopyFile = (input = '') => {
    if (status !== 'success') {
      Other.toast('网页结果生成中，请稍后再试', 'error');
      return;
    }

    const plainText = markdownToPlainText(input);
    const copied = copy(plainText);
    copied && Other.toast('复制成功', 'success');
  };

  const handleSaveFile = async () => {
    let _fileName = title || `${+new Date()}.html`;
    if (!_fileName.endsWith('.html')) {
      _fileName = _fileName + '.html';
    }

    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    HelperCore.saveFile(url, _fileName);
    URL.revokeObjectURL(url);
  };

  const copyUrl = () => {
    const copied = copy(share_url || url);
    copied && Other.toast('链接已复制', 'success');
  };

  const onSegmentedChange = value => {
    HelperCore.log('onSegmentedChange', value, status);
    setCurrentVal(value);
  };

  const darkMode = currentVal == 'code' || mcpStore.isDarkMode;

  const empty_content = !content || content.length === 0;

  return (
    <section
      className={[
        'overflow-hidden rounded-10px',
        'size-full py-20px flex flex-col ',
        currentVal !== 'code' ? 'bg-white' : 'bg-[#1E1F22]',
        currentVal !== 'code' ? 'pr-4px' : 'pr-4px',
        empty_content ? 'min-h-260px' : '',
      ]}
    >
      {
        <div className="w-full h-full flex flex-col relative">
          <header className="pb-16px px-24px tabs border-bottom-0px border-[#e6e6e6] flex justify-left items-center">
            {mcpStore.hasHtmlFinished || true ? (
              <Segmented
                options={actionItems}
                value={currentVal}
                onChange={onSegmentedChange}
              />
            ) : null}
            <div className="w-0 flex-grow"></div>

            {currentVal === 'result_web' && status === 'success' ? (
              <>
                <div 
                  className={`
                    h-28px box-border rounded-6px border-1px border-black
                    border-opacity-16 leading-26px px-10px cursor-pointer
                    hover:bg-[rgba(0,0,0,0.03)] mr-8px text-14px text-black
                    flex items-center
                    ${HelperCore.isPhone() ? "border-0px px-0px" : ""}
                  `}
                  onClick={() => {
                    if(isMcpShare && shareId) {
                      fullScreenModalStore.setData({
                        show: true, 
                        type: "html",
                        data: {
                          shareUrl: share_url,
                          shareId: shareId,
                          noDownload: true
                        }
                      });
                      return
                    }
                    fullScreenModalStore.setData({
                      show: true, 
                      type: "html",
                      data: {
                        shareUrl: share_url,
                        shareId: shareId,
                        content: content,
                        noDownload: true
                      }
                    });
                  }}
                >
                  <SVGPureIcon
                    name="full_screen_modal_full2"
                    className={`size-16px ${!HelperCore.isPhone() ? "mr-6px" : ""}`}
                  />
                  {
                    !HelperCore.isPhone()
                    ? "全屏"
                    : null
                  }
                </div>
                {share_url ? (
                  <Tooltip 
                    title={
                      HelperCore.isPhone()
                      ? null
                      : <QrcodeShare url={share_url} />
                    } 
                    color="white"
                    placement="bottomLeft"
                    arrow={false}
                    getPopupContainer={node => node}
                    destroyTooltipOnHide
                  >
                    <div 
                      className={`
                        h-28px box-border rounded-6px border-1px border-black
                        border-opacity-16 leading-26px px-10px cursor-pointer
                        hover:bg-[rgba(0,0,0,0.03)] text-14px text-black
                        flex items-center
                        ${HelperCore.isPhone() ? "border-0px px-0px" : ""}
                      `}
                      onClick={() => {
                        const copied = copy(share_url);     
                        copied && Other.toast('复制成功', "success");
                      }}
                    >
                      <SVGPureIcon
                        name="full_screen_modal_share2"
                        className={`size-16px ${!HelperCore.isPhone() ? "mr-6px" : ""}`}
                      />
                      {
                        !HelperCore.isPhone()
                        ? "分享"
                        : null
                      }
                    </div>
                  </Tooltip>
                ) : null}
                {/* <div
                  className={[
                    'px-10px py-4px mr-24px cursor-pointer text-[#000] hover:bg-[rgba(0,0,0,0.03)]',
                    'border-1 border-[rgba(0,0,0,0.16)] rounded-[6px]',
                  ]}
                  onClick={() => {
                    handleSaveFile(content);
                  }}
                >
                  {isPhone ? '保存' : '保存为HTML'}
                </div> */}
              </>
            ) : null}

            {currentVal === 'code' ? (
              <>
                <div
                  className={[
                    'px-10px py-4px mr-6px cursor-pointer text-[#FFF]',
                    'border-1 border-[rgba(255,255,255,0.1)] bg-[#444] hover:bg-[#666] rounded-[6px]',
                  ]}
                  onClick={() => {
                    handleCopyFile(content);
                  }}
                >
                  复制
                </div>
              </>
            ) : null}
          </header>
          <>
            {status === 'failed' ? (
              <div
                className={`flex flex-col h-400px items-center justify-center`}
              >
                <img
                  className="w-190px"
                  src="https://p5.ssl.qhimg.com/t11098f6bcda6a604f3f799138d.png"
                />
                <div className="text-14px leading-30px text-black text-opacity-50">
                  创建网站失败
                </div>
              </div>
            ) : empty_content ? (
              <Skeleton
                className={[
                  currentVal === 'code' ? 'antd-dark pr-24px' : 'pr-24px',
                  'px-24px mt-12px',
                ]}
                active
              />
            ) : currentVal === 'result_web' ? (
              isMcpShare && shareId ? (
                <div
                  className={`flex-1 overflow-hidden mt-8px ${!isPhone ? 'px-24px' : ''}`}
                  ref={refScroll}
                >
                  <iframe
                    ref={refIframe}
                    src={`/api/html/detail?id=${shareId}`}
                    className="size-full"
                    style={{
                      minHeight: `calc(100vh - 64px - 72px - 48px - 100px)`,
                      aspectRatio: 9/12
                    }}
                    sandbox="allow-scripts allow-same-origin allow-popups allow-downloads allow-modals allow-popups-to-escape-sandbox"
                  />
                </div>
              ) : (
                <div
                  ref={refScroll}
                  className={`mt-8px ${!isPhone ? 'px-24px' : ''}`}
                >
                  <ScaleBox>
                    <iframe
                      ref={refIframe}
                      src="about:srcdoc"
                      // className="size-full"
                      // className="w-1152px h-656px"
                      className="w-618px"
                      style={{
                        aspectRatio: 9/12
                      }}
                      srcDoc={HtmlViewTemplate.replace(
                        '{html_content_holder}',
                        content
                      )}
                      sandbox="allow-scripts allow-same-origin allow-popups allow-downloads allow-modals allow-popups-to-escape-sandbox"
                    />
                  </ScaleBox>
                </div>
              )
            ) : (
              <ScrollView
                scrollbarThin={true}
                enableAutoScroll={true}
                className={`!h-max-500px pl-15px`}
              >
                {true && <CodeBlock language={'html'} content={content} darkMode={darkMode} inputting={event.status == "running"} />}
                {false && <Markdown
                  typing={false}
                  text={
                    event.status == "running"
                      ? '```text\n' + content?.trim() + '\n```'
                      : '```html\n' + content?.trim() + '\n```'
                  }
                  hideLanguage
                  highlighterConfig={highlighterConfig}
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const LANGUAGE_RE = /language-(\w+)/;
                      const match = LANGUAGE_RE.exec(className);
                      if (match) {
                        return (
                          <code
                            {...props}
                            className={[
                              'whitespace-pre-wrap text-[14px] rounded-[3px] leading-relaxed',
                              className,
                            ]}
                          >
                            <SyntaxHighlighter
                              {...props}
                              wrapLongLines
                              className={`leading-relaxed ${highlighterConfig?.className ?? 'highlighter-box'}`}
                              customStyle={{
                                fontFamily:
                                  'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                              }}
                              children={String(children).replace(/\n$/, '')}
                              {...highlighterConfig}
                              language={match[1]}
                            />
                          </code>
                        );
                      }
                    },
                  }}
                />}
              </ScrollView>
            )}
          </>
        </div>
      }
    </section>
  );
});

export default CardHtml;
